import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Plus, 
  Sparkles,
  Calendar,
  ExternalLink,
  ClipboardCheck,
  Clock3,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { celebrationService } from '@/lib/api/celebration.service';
import { getEmojiForType, getLabelForType, getSubtypeLabel } from '@/config/celebrationRegistry';
import { guestService } from '@/lib/api/guest.service';
import { celebrationInvitationService } from '@/lib/api/celebration-invitation.service';
import { useAuth } from '@/hooks/useAuth';
import { useSessionContext } from '@om/shared-auth';
import type { Celebration } from '@/types/celebration.types';
import type { CelebrationDraftEnvelope } from '@om/shared-types';
import type { CelebrationInvitationSummary } from '@/types/celebration-invitation.types';
import type { InvitationGroups, InvitationSummary } from '@/types/invitation.types';

export const SmartDashboard: React.FC = () => {
  const { t } = useTranslation(['celebrations', 'common']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const sessionContext = useSessionContext();
  const doesSessionExist = !sessionContext.loading && sessionContext.doesSessionExist;
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const [drafts, setDrafts] = useState<CelebrationDraftEnvelope[]>([]);
  const [deletingDraftId, setDeletingDraftId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [loadingCelebrationInvitations, setLoadingCelebrationInvitations] = useState(true);
  const [myInvitations, setMyInvitations] = useState<InvitationGroups>({ upcoming: [], past: [] });
  const [celebrationInvitations, setCelebrationInvitations] = useState<CelebrationInvitationSummary[]>([]);
  const [respondingInvitationId, setRespondingInvitationId] = useState<number | null>(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<InvitationSummary | null>(null);
  const [rsvpStatus, setRsvpStatus] = useState('new');
  const [rsvpNotes, setRsvpNotes] = useState('');
  const [rsvpMealPreference, setRsvpMealPreference] = useState('');
  const [rsvpDietaryRestrictions, setRsvpDietaryRestrictions] = useState('');
  const [rsvpPlusOneName, setRsvpPlusOneName] = useState('');
  const [updatingRsvp, setUpdatingRsvp] = useState(false);

  const refreshInvitations = useCallback(async () => {
    if (!doesSessionExist) {
      setLoadingInvitations(false);
      return;
    }

    try {
      setLoadingInvitations(true);
      const invitations = await guestService.getMyInvitations();
      setMyInvitations(invitations);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  }, [doesSessionExist]);

  const refreshCelebrationInvitations = useCallback(async () => {
    if (!doesSessionExist) {
      setLoadingCelebrationInvitations(false);
      return;
    }

    try {
      setLoadingCelebrationInvitations(true);
      const invitations = await celebrationInvitationService.getMyInvitations();
      setCelebrationInvitations(invitations);
    } catch (error) {
      console.error('Failed to load celebration invitations:', error);
    } finally {
      setLoadingCelebrationInvitations(false);
    }
  }, [doesSessionExist]);

  const handleDeleteDraft = async (draftId: string) => {
    if (!window.confirm('Delete this celebration draft? This cannot be undone.')) return;

    try {
      setDeletingDraftId(draftId);
      await celebrationService.deleteDraft(draftId);
      setDrafts((current) => current.filter((draft) => draft.draft_id !== draftId));
    } catch (error) {
      console.error('Failed to delete celebration draft:', error);
    } finally {
      setDeletingDraftId(null);
    }
  };

  useEffect(() => {
    if (sessionContext.loading) {
      return;
    }

    if (!doesSessionExist) {
      setLoading(false);
      setLoadingDrafts(false);
      setLoadingInvitations(false);
      setLoadingCelebrationInvitations(false);
      setCelebrations([]);
      setDrafts([]);
      setMyInvitations({ upcoming: [], past: [] });
      setCelebrationInvitations([]);
      return;
    }

    const loadData = async () => {
      try {
        const [celebrationList, draftList] = await Promise.all([
          celebrationService.getCelebrations(),
          celebrationService.listDrafts(),
        ]);
        setCelebrations(celebrationList);
        setDrafts(draftList);
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
        setLoadingDrafts(false);
      }

      await Promise.all([refreshInvitations(), refreshCelebrationInvitations()]);
    };
    loadData();
  }, [sessionContext.loading, doesSessionExist, refreshInvitations, refreshCelebrationInvitations]);

  const handleCelebrationInvitationDecision = async (
    invitationId: number,
    decision: 'accepted' | 'declined',
  ) => {
    try {
      setRespondingInvitationId(invitationId);
      const response = await celebrationInvitationService.respondToInvitation(invitationId, decision);
      
      // Check if there's a redirect hint (e.g., for event managers accepting invites)
      if (decision === 'accepted' && response?.redirectUrl) {
        navigate(response.redirectUrl);
        return;
      }
      
      await refreshCelebrationInvitations();
    } catch (error) {
      console.error('Failed to respond to celebration invitation:', error);
      alert('Failed to update invitation. Please try again.');
    } finally {
      setRespondingInvitationId(null);
    }
  };

  const guestLandingBaseUrl = (() => {
    const configuredBaseUrl = import.meta.env.VITE_GUEST_LANDING_BASE_URL?.trim();
    if (configuredBaseUrl) {
      return configuredBaseUrl.replace(/\/$/, '');
    }

    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5437`;
    }

    return window.location.origin;
  })();

  const getInvitationUrl = (invitation: InvitationSummary) => {
    if (!invitation.celebration_slug) {
      return '';
    }

    const baseUrl = `${guestLandingBaseUrl}/g/${invitation.celebration_slug}`;
    if (!invitation.invitation_token) {
      return baseUrl;
    }

    return `${baseUrl}?token=${encodeURIComponent(invitation.invitation_token)}`;
  };

  const getRsvpBadgeVariant = (status: string): 'accepted' | 'declined' | 'pending' | 'new' | 'invited' => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'accepted' || normalizedStatus === 'attending') {
      return 'accepted';
    }

    if (normalizedStatus === 'declined') {
      return 'declined';
    }

    if (normalizedStatus === 'maybe' || normalizedStatus === 'pending') {
      return 'pending';
    }

    if (normalizedStatus === 'invited') {
      return 'invited';
    }

    return 'new';
  };

  const getRsvpLabel = (status: string): string => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'accepted' || normalizedStatus === 'attending') {
      return '🟢 Attending';
    }

    if (normalizedStatus === 'declined') {
      return '🔴 Declined';
    }

    if (normalizedStatus === 'maybe') {
      return '🟠 Maybe';
    }

    return '🟡 Pending';
  };

  const formatInvitationDate = (dateValue?: string) => {
    if (!dateValue) {
      return 'Date TBD';
    }

    const invitationDate = new Date(dateValue);
    if (Number.isNaN(invitationDate.getTime())) {
      return 'Date TBD';
    }

    const formattedDate = invitationDate.toLocaleDateString();
    const now = new Date();
    const oneDay = 24 * 60 * 60 * 1000;
    const differenceInDays = Math.round((invitationDate.getTime() - now.getTime()) / oneDay);
    const relativeFormatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

    return `${formattedDate} (${relativeFormatter.format(differenceInDays, 'day')})`;
  };

  const openUpdateDialog = (invitation: InvitationSummary) => {
    setSelectedInvitation(invitation);
    const normalizedStatus = invitation.rsvp_status?.toLowerCase();
    if (normalizedStatus === 'accepted') {
      setRsvpStatus('attending');
    } else {
      setRsvpStatus(normalizedStatus || 'new');
    }
    setRsvpNotes('');
    setRsvpMealPreference(invitation.meal_preference || '');
    setRsvpDietaryRestrictions(invitation.dietary_restrictions || '');
    setRsvpPlusOneName(invitation.plus_one_name || '');
    setIsUpdateDialogOpen(true);
  };

  const handleRsvpUpdate = async () => {
    if (!selectedInvitation) {
      return;
    }

    try {
      setUpdatingRsvp(true);
      await guestService.updateInvitationRSVP(selectedInvitation.guest_id, {
        rsvp_status: rsvpStatus,
        notes: rsvpNotes.trim() || undefined,
        meal_preference: rsvpMealPreference || undefined,
        dietary_restrictions: rsvpDietaryRestrictions.trim() || undefined,
        plus_one_name: rsvpPlusOneName.trim() || undefined,
      });

      setIsUpdateDialogOpen(false);
      await refreshInvitations();
    } catch (error) {
      console.error('Failed to update RSVP:', error);
      alert('Failed to update RSVP. Please try again.');
    } finally {
      setUpdatingRsvp(false);
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="wide"
        title="Loading dashboard"
        description="Please wait while we prepare your dashboard."
      />
    );
  }

  return (
    <PageContainer width="wide" className="space-y-8 animate-in fade-in duration-500">
      <AppBreadcrumbs />

      {/* Welcome Section */}
      <section className="text-center sm:text-left space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-celebration-purple">{user?.full_name?.split(' ')[0] || 'User'}</span>!
        </h1>
        <p className="text-muted-foreground text-lg">
          Ready to plan your next memorable event?
        </p>
      </section>

      {/* Quick Links */}
      <section className="flex flex-wrap gap-2">
        <Link to="/celebrations/new">
          <Button size="sm">
            <Plus className="h-4 w-4 mr-2" />
            New Celebration
          </Button>
        </Link>
        <Link to="/managed-events">
          <Button variant="outline" size="sm">
            <Calendar className="h-4 w-4 mr-2" />
            Events I Manage
          </Button>
        </Link>
      </section>

      {/* My Celebrations */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            {t('celebrations:list')}
          </h2>
        </div>

        {!loadingDrafts && drafts.length > 0 && (
          <div className="mb-4 space-y-2">
            {drafts.map((draft) => {
              const title = typeof draft.draft.title === 'string' && draft.draft.title.trim()
                ? draft.draft.title
                : 'Untitled celebration';
              const type = typeof draft.draft.celebration_type === 'string'
                ? draft.draft.celebration_type
                : 'other';
              const resumePath = draft.mode === 'ai'
                ? `/celebrations/new?mode=chat&draft=${encodeURIComponent(draft.draft_id)}`
                : `/quick?draft=${encodeURIComponent(draft.draft_id)}`;

              return (
                <Card key={draft.draft_id} className="border-primary/20 bg-primary/[0.03]">
                  <CardContent className="flex flex-wrap items-center gap-3 p-4">
                    <Clock3 className="h-5 w-5 flex-shrink-0 text-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="truncate text-sm font-semibold">{title}</h3>
                        <Badge variant="outline" className="text-[10px]">
                          Draft · {draft.mode === 'ai' ? 'AI Chat' : 'Quick'}
                        </Badge>
                        {draft.draft.celebration_type && (
                          <Badge variant="secondary" className="text-[10px]">
                            {getLabelForType(type)}
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Step {(draft.resume_state.current_step ?? 0) + 1} · Updated {new Date(draft.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => navigate(resumePath)}>
                      Continue
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Delete draft"
                      aria-label={`Delete ${title} draft`}
                      disabled={deletingDraftId === draft.draft_id}
                      onClick={() => void handleDeleteDraft(draft.draft_id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {celebrations.length > 0 ? (
          <div className="space-y-2">
            {celebrations.map((celebration) => (
              <Card
                key={celebration.id}
                className="border transition-colors cursor-pointer hover:border-primary/50 hover:shadow-sm"
                onClick={() => navigate(`/celebrations/${celebration.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl leading-none flex-shrink-0">{getEmojiForType(celebration.celebration_type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm truncate">{celebration.title}</h3>
                        <Badge variant="outline" className="capitalize">
                          {getLabelForType(celebration.celebration_type)}
                        </Badge>
                        {celebration.celebration_subtype && (
                          <Badge variant="secondary" className="text-[10px]">
                            {getSubtypeLabel(celebration.celebration_type, celebration.celebration_subtype)}
                          </Badge>
                        )}
                      </div>
                      {celebration.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{celebration.description}</p>
                      )}
                      <div className="flex items-center gap-1 mt-1.5 text-xs text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>{celebration.start_date ? new Date(celebration.start_date).toLocaleDateString() : 'Date TBD'}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {celebrations.length < 3 && (
              <Card className="border-dashed border-primary/30 hover:border-primary/50 transition-colors">
                <CardContent className="p-4">
                  <button
                    type="button"
                    onClick={() => navigate('/celebrations/new')}
                    className="w-full flex items-center gap-3 text-left"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Plus className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{t('celebrations:new')}</p>
                      <p className="text-xs text-muted-foreground">Create a new celebration</p>
                    </div>
                  </button>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="py-8 text-center flex flex-col items-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-1">No active celebrations</h3>
              <p className="text-muted-foreground mb-4 max-w-md">You don't have any upcoming celebrations. Create one to get started!</p>
              <Button onClick={() => navigate('/celebrations/new')}>Create Celebration</Button>
            </CardContent>
          </Card>
        )}
      </section>

      {!loadingCelebrationInvitations && celebrationInvitations.length > 0 && (
        <Card className="border-l-4 border-l-emerald-600">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-emerald-600" />
              Invitations Awaiting Your Response
            </CardTitle>
            <CardDescription>
              Review each invitation and decide whether to accept or decline it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {celebrationInvitations.map((invitation) => (
              <div key={invitation.id} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-slate-900">{invitation.celebration_title}</div>
                    <div className="text-sm text-slate-600 mt-1">
                      {invitation.invite_type === 'team_member'
                        ? `Team invitation as ${invitation.team_role?.replace('_', ' ') || 'team member'}`
                        : `Celebrant invitation as ${invitation.celebrant_role || 'celebrant'}`}
                      {invitation.is_surprise ? ' · Surprise' : ''}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCelebrationInvitationDecision(invitation.id, 'declined')}
                      disabled={respondingInvitationId === invitation.id}
                    >
                      Decline
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleCelebrationInvitationDecision(invitation.id, 'accepted')}
                      disabled={respondingInvitationId === invitation.id}
                    >
                      {respondingInvitationId === invitation.id ? t('common:status.saving') : 'Accept'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              My Invitations
            </CardTitle>
            <CardDescription>Invitations grouped into Upcoming and Past.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {loadingInvitations ? (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Upcoming</h3>
                  {myInvitations.upcoming.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No upcoming invitations.</p>
                  ) : (
                    <div className="space-y-2">
                      {myInvitations.upcoming.map((invitation) => {
                        const invitationUrl = getInvitationUrl(invitation);
                        return (
                          <div key={invitation.guest_id} className="rounded-md border p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-sm">{invitation.celebration_title}</p>
                                <p className="text-xs text-muted-foreground">{formatInvitationDate(invitation.invitation_date)}</p>
                                {(invitation.meal_preference || invitation.plus_one_name) && (
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                    {invitation.meal_preference && (
                                      <span className="text-xs text-muted-foreground">🍽 {invitation.meal_preference}</span>
                                    )}
                                    {invitation.plus_one_name && (
                                      <span className="text-xs text-muted-foreground">+1 {invitation.plus_one_name}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Badge variant={getRsvpBadgeVariant(invitation.rsvp_status)}>{getRsvpLabel(invitation.rsvp_status)}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!invitationUrl}
                                onClick={() => {
                                  if (invitationUrl) {
                                    window.open(invitationUrl, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                              >
                                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                View Event
                              </Button>
                              <Button size="sm" onClick={() => openUpdateDialog(invitation)}>
                                Update RSVP
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <h3 className="text-sm font-semibold">Past</h3>
                  {myInvitations.past.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No past invitations.</p>
                  ) : (
                    <div className="space-y-2">
                      {myInvitations.past.map((invitation) => {
                        const invitationUrl = getInvitationUrl(invitation);
                        return (
                          <div key={invitation.guest_id} className="rounded-md border p-3 space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <p className="font-medium text-sm">{invitation.celebration_title}</p>
                                <p className="text-xs text-muted-foreground">{formatInvitationDate(invitation.invitation_date)}</p>
                                {(invitation.meal_preference || invitation.plus_one_name) && (
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                    {invitation.meal_preference && (
                                      <span className="text-xs text-muted-foreground">🍽 {invitation.meal_preference}</span>
                                    )}
                                    {invitation.plus_one_name && (
                                      <span className="text-xs text-muted-foreground">+1 {invitation.plus_one_name}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Badge variant={getRsvpBadgeVariant(invitation.rsvp_status)}>{getRsvpLabel(invitation.rsvp_status)}</Badge>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!invitationUrl}
                                onClick={() => {
                                  if (invitationUrl) {
                                    window.open(invitationUrl, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                              >
                                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                View Event
                              </Button>
                              <Button size="sm" onClick={() => openUpdateDialog(invitation)}>
                                Update RSVP
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
          </Card>

          <Link
            to="/contacts"
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-lg"
          >
            <Card className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500 group h-full">
              <CardHeader className="pb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center mb-2 text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                  <Users className="h-6 w-6" />
                </div>
                <CardTitle>Contacts</CardTitle>
                <CardDescription>Manage your guest list and vendors</CardDescription>
              </CardHeader>
            </Card>
          </Link>
        </div>
      </section>

      <Dialog open={isUpdateDialogOpen} onClose={() => setIsUpdateDialogOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update RSVP</DialogTitle>
            <DialogDescription>
              {selectedInvitation ? `Update your RSVP for ${selectedInvitation.celebration_title}.` : 'Update your RSVP status.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="rsvp-status">RSVP Status</Label>
              <select
                id="rsvp-status"
                title="RSVP status"
                className="w-full rounded-md border border-input bg-background h-9 px-3 text-sm"
                value={rsvpStatus}
                onChange={(event) => setRsvpStatus(event.target.value)}
              >
                <option value="attending">Attending</option>
                <option value="maybe">Maybe</option>
                <option value="declined">Can&apos;t Make It</option>
                <option value="new">Pending</option>
              </select>
            </div>

            {(rsvpStatus === 'attending' || rsvpStatus === 'maybe') && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="rsvp-meal">Meal Preference</Label>
                  <select
                    id="rsvp-meal"
                    title="Meal preference"
                    className="w-full rounded-md border border-input bg-background h-9 px-3 text-sm"
                    value={rsvpMealPreference}
                    onChange={(event) => setRsvpMealPreference(event.target.value)}
                  >
                    <option value="">No Preference</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Non-Vegetarian">Non-Vegetarian</option>
                    <option value="Gluten-Free">Gluten-Free</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="rsvp-dietary">Dietary Restrictions / Allergies</Label>
                  <textarea
                    id="rsvp-dietary"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    rows={2}
                    value={rsvpDietaryRestrictions}
                    onChange={(event) => setRsvpDietaryRestrictions(event.target.value)}
                    placeholder="e.g., No nuts, lactose intolerant"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="rsvp-plus-one">Plus-One Name (optional)</Label>
                  <Input
                    id="rsvp-plus-one"
                    value={rsvpPlusOneName}
                    onChange={(event) => setRsvpPlusOneName(event.target.value)}
                    placeholder="Name of your plus-one"
                  />
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label htmlFor="rsvp-notes">Notes (optional)</Label>
              <textarea
                id="rsvp-notes"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                rows={2}
                value={rsvpNotes}
                onChange={(event) => setRsvpNotes(event.target.value)}
                placeholder="Add any RSVP notes"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUpdateDialogOpen(false)} disabled={updatingRsvp}>
              Cancel
            </Button>
            <Button onClick={handleRsvpUpdate} disabled={updatingRsvp || !selectedInvitation}>
              {updatingRsvp ? 'Saving...' : 'Save RSVP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
